/** The sidebar draws before this module's chunk loads, so its labels are registered up front (see messages.nav.ts). */
export const portalNavEn = {
  "portal.settings.activities": "Member activities",
  "portal.settings.activitiesHint": "Off by default. Enable after event charges, dining procedures and member launch are approved.",
  "portal.settings.eventsBooking": "Allow online event sign-ups",
  "portal.settings.diningRequests": "Allow online dining requests",
  "portal.nav.access": "Member access",
  "portal.nav.settings": "Member portal",
  "portal.nav.app": "Member app",
  "portal.nav.staffViews": "Staff views",
  "portal.nav.memberRequests": "Member requests",
  "portal.mr.waitingCount_one": "{count} request waiting",
  "portal.mr.waitingCount_other": "{count} requests waiting",

  // "View portal as member" is shown on the member and household pages, whose chunk loads before this module's screens do.
  "portal.viewAs.button": "View portal as member",
  "portal.viewAs.title": "View the member portal as {name}",
  "portal.viewAs.body": "You will see and be able to do everything {name} can in the member portal, including paying their bill with a card they read to you. You never see or change how they sign in. The member is not told. The reason below, your name and everything you do are recorded in the audit log.",
  "portal.viewAs.reason": "Why are you opening their portal?",
  "portal.viewAs.reasonHint": "At least {count} characters, for example: member phoned to pay the October bill.",
  "portal.viewAs.reasonShort": "Write at least {count} characters.",
  "portal.viewAs.start": "Continue",
  "portal.viewAs.readyTitle": "Their portal is ready",
  "portal.viewAs.readyBody": "Open it now: the link works once and for one minute. The session lasts {minutes} minutes, or until you press End session in the red bar at the top.",
  "portal.viewAs.open": "Open the member portal in a new tab",
  "portal.viewAs.opened": "Opened in a new tab. Close this window when you are done.",
  "portal.viewAs.staffOnly": "Look-only view: this member has no portal account; use the staff screens to make changes. Nothing is created for them, nothing is sent to them, and they are not told. Inviting them is a separate step.",
} as const;

export const portalEn = {
  "apiError.request_not_routed": "General messages go to another role at this club.",
  "apiError.request_closed": "This message is already closed.",
  "portal.mr.title": "Member requests",
  "portal.mr.subtitle": "House and cottage work and messages that members send from the portal. A job is scheduled and finished in its work order; a reply marked for the member shows in their portal. Nothing is emailed to them yet.",
  "portal.mr.filter.status": "Show",
  "portal.mr.filter.open": "Open",
  "portal.mr.filter.new": "Waiting for staff",
  "portal.mr.filter.closed": "Closed",
  "portal.mr.filter.all": "All",
  "portal.mr.filter.kind": "Kind",
  "portal.mr.filter.allKinds": "All kinds",
  "portal.mr.kind.maintenance": "House or cottage work",
  "portal.mr.kind.message": "Message",
  "portal.mr.status.requested": "Requested",
  "portal.mr.status.scheduled": "Scheduled",
  "portal.mr.status.in_progress": "In progress",
  "portal.mr.status.done": "Done",
  "portal.mr.status.cancelled": "Cancelled",
  "portal.mr.status.new": "New",
  "portal.mr.status.open": "Answered",
  "portal.mr.status.closed": "Closed",
  "portal.mr.col.request": "Request",
  "portal.mr.col.from": "From",
  "portal.mr.col.status": "Status",
  "portal.mr.col.sent": "Sent",
  "portal.mr.none": "No open requests",
  "portal.mr.noneBody": "When a member asks for work or writes to the staff in the portal, it appears here.",
  "portal.mr.noMatches": "No requests match these filters",
  "portal.mr.noMatchesBody": "Try another status or kind.",
  "portal.mr.thread": "Conversation",
  "portal.mr.staffAuthor": "{name} (staff)",
  "portal.mr.enteredByStaff": "Entered by a staff member in a staff view of the member's portal.",
  "portal.mr.reply": "Reply to the member",
  "portal.mr.replyHint": "The member reads this in the portal. They are not emailed yet.",
  "portal.mr.send": "Send reply",
  "portal.mr.replied": "Reply sent.",
  "portal.mr.close": "Close message",
  "portal.mr.closed": "Message closed.",
  "portal.mr.noMoreReplies": "This message is closed. The member can send a new one.",
  "portal.settings.requests": "Member requests",
  "portal.settings.requestsBody": "Choose who answers the general messages members send from the portal. House-work requests always go through the work orders.",
  "portal.settings.messageRole": "General messages go to",
  "portal.settings.messageRoleAnyone": "Anyone with access to member requests",
  "portal.settings.messageRoleHint": "Club managers can always answer them.",
  "portal.requests.madeInStaffView": "Made by {name} in a staff view of the member's portal. Someone else must approve it.",
  "portal.staffViews.title": "Staff views of the member portal",
  "portal.staffViews.subtitle": "Each time a staff member opened the member portal as a member: who, why, when and how many changes they made. The member is not notified. Press Audit trail for every action in one session.",
  "portal.staffViews.activeOnly": "Only sessions still open",
  "portal.staffViews.empty": "No staff views yet",
  "portal.staffViews.emptyBody": "When a staff member uses View portal as member on a member's page, it is listed here.",
  "portal.staffViews.col.staff": "Staff member",
  "portal.staffViews.col.member": "Member",
  "portal.staffViews.col.reason": "Reason",
  "portal.staffViews.col.started": "Started",
  "portal.staffViews.col.ended": "Ended",
  "portal.staffViews.col.state": "Status",
  "portal.staffViews.col.actions": "Changes made",
  "portal.staffViews.audit": "Audit trail",
  "portal.staffViews.end": "End now",
  "portal.staffViews.ended": "Ended the session for {name}.",
  "portal.staffViews.state.unused": "Never opened",
  "portal.staffViews.state.pending": "Waiting to open",
  "portal.staffViews.state.active": "Open",
  "portal.staffViews.state.ended": "Ended",
  "portal.staffViews.state.expired": "Timed out",
  "portal.staffViews.why.staff": "ended by staff",
  "portal.staffViews.why.expired": "30 minutes passed",
  "portal.staffViews.why.replaced": "replaced by a newer session",
  "portal.staffViews.why.staff_signed_out": "the staff member signed out",
  "portal.staffViews.why.permission_revoked": "permission removed",
  "portal.staffViews.why.access_revoked": "the member's access or sessions were revoked",
  "portal.staffViews.why.member_unavailable": "the member's access ended",

  "portal.tabs": "Member access views",
  "portal.tabs.access": "Access",
  "portal.tabs.requests": "Change requests",
  "portal.tabs.payments": "Payments needing attention",
  "portal.tabs.paymentsCount_one": "Payments needing attention ({count})",
  "portal.tabs.paymentsCount_other": "Payments needing attention ({count})",
  "portal.tabs.requestsCount_one": "Change requests ({count})",
  "portal.tabs.requestsCount_other": "Change requests ({count})",

  "portal.access.title": "Member access",
  "portal.access.subtitle": "Who can sign in to the member portal and the apps. A member is invited here; staff never see a member's password or PIN.",
  "portal.access.empty": "No members match",
  "portal.access.emptyBody": "Change the filters, or add members first.",
  "portal.filter.search": "Search members",
  "portal.filter.access": "Access",
  "portal.filter.membership": "Membership",
  "portal.filter.current": "Current members",
  "portal.filter.inactive": "Inactive members",
  "portal.status.none": "Not invited",
  "portal.status.invited": "Invited",
  "portal.status.active": "Active",
  "portal.status.revoked": "Revoked",
  "portal.col.member": "Member",
  "portal.col.email": "Email",
  "portal.col.status": "Access",
  "portal.col.signIn": "Signs in with",
  "portal.col.last": "Last sign-in",
  "portal.col.actions": "Actions",
  "portal.noEmail": "No email on file",
  "portal.method.password": "Password",
  "portal.method.pin": "PIN",
  "portal.method.pinLocked": "PIN locked",
  "portal.method.totp": "Two-step",
  "portal.method.link": "Email link",

  "portal.action.invite": "Invite",
  "portal.action.resend": "New link",
  "portal.action.inviteFor": "Invite {name}",
  "portal.action.resendFor": "Send {name} a new link",
  "portal.action.resetPin": "Reset PIN",
  "portal.action.resetPinFor": "Reset the PIN of {name}",
  "portal.action.resetPassword": "Reset password",
  "portal.action.resetPasswordFor": "Reset the password of {name}",
  "portal.action.resetTwoFactor": "Reset two-step",
  "portal.action.resetTwoFactorFor": "Reset two-step sign-in for {name}",
  "portal.action.revoke": "Revoke",
  "portal.action.revokeFor": "Revoke the access of {name}",

  "portal.invite.title": "Invite {name}",
  "portal.invite.again": "Send {name} a new link",
  "portal.invite.body": "The member gets a one-time link to choose how they will sign in. It is shown to you here, and emailed if this server can send email.",
  "portal.invite.email": "Email address",
  "portal.invite.emailHint": "The address the link goes to and the member signs in with. Defaults to the one on their record.",
  "portal.invite.send": "Email the link to the member",
  "portal.invite.submit": "Create the link",
  "portal.invite.sentTitle": "Link for {name}",

  "portal.link.body": "Give this one-time link to {name}. It is not shown again.",
  "portal.link.label": "One-time link",
  "portal.link.emailed": "It was also emailed to {email}.",
  "portal.link.notEmailed": "It was not emailed: copy it and send it to the member yourself.",
  "portal.link.expires": "The link works once and expires {date}.",
  "portal.link.copy": "Copy the link",
  "portal.link.sent": "A link was emailed to {name}.",
  "portal.link.ready": "A link for {name} is ready to copy.",

  "portal.revoke.title": "Revoke access for {name}?",
  "portal.revoke.body": "They are signed out on every device and every link stops working. Inviting them again restores access.",
  "portal.revoke.reason": "Reason (optional)",
  "portal.revoke.confirm": "Revoke access",
  "portal.revoke.done": "{name}'s access was revoked.",

  "portal.resetPin.title": "Reset the PIN of {name}?",
  "portal.resetPin.body": "The old PIN stops working now, and the member gets a one-time link to choose a new one.",
  "portal.resetPin.linkTitle": "Link to choose a new PIN",
  "portal.resetPassword.title": "Reset the password of {name}?",
  "portal.resetPassword.body": "The member is signed out everywhere and gets a one-time link to choose a new password.",
  "portal.resetPassword.linkTitle": "Link to choose a new password",
  "portal.reset.confirm": "Create the link",
  "portal.resetTwoFactor.title": "Turn off two-step sign-in for {name}?",
  "portal.resetTwoFactor.body": "For a member who lost their phone and their recovery codes. They are signed out and can set it up again.",
  "portal.resetTwoFactor.confirm": "Turn it off",
  "portal.resetTwoFactor.done": "Two-step sign-in was turned off for {name}.",

  "portal.bulk.open": "Invite many members",
  "portal.bulk.title": "Invite many members",
  "portal.bulk.body": "Everyone who matches and has an email address gets a link by email. Members with no email, or whose address another member already uses, are skipped and listed.",
  "portal.bulk.who": "Who",
  "portal.bulk.active": "Current members",
  "portal.bulk.inactive": "Inactive members",
  "portal.bulk.resend": "Also send a new link to members who were invited but have not finished",
  "portal.bulk.preview": "Preview",
  "portal.bulk.send_one": "Send {count} invitation",
  "portal.bulk.send_other": "Send {count} invitations",
  "portal.bulk.result": "{invited} of {considered} members would be invited; {skipped} skipped.",
  "portal.bulk.sample": "Each invited member gets this email, with their own name and link:",
  "portal.bulk.skipped_one": "{count} member skipped",
  "portal.bulk.skipped_other": "{count} members skipped",
  "portal.bulk.sent_one": "{count} invitation sent.",
  "portal.bulk.sent_other": "{count} invitations sent.",
  "portal.skip.already_has_access": "already has access",
  "portal.skip.no_email": "no email address",
  "portal.skip.email_in_use": "another member uses that address",
  "portal.skip.not_current": "not a current member",
  "portal.skip.revoked": "access was revoked",

  "portal.requests.title": "Change requests",
  "portal.requests.subtitle": "Contact details members asked to change. Nothing is applied until you approve it.",
  "portal.requests.none": "Nothing is waiting",
  "portal.requests.noneBody": "Requests appear here when a member asks to change their email, phone or address.",
  "portal.requests.approve": "Approve",
  "portal.requests.approveFor": "Approve the change for {name}",
  "portal.requests.reject": "Reject",
  "portal.requests.rejectFor": "Reject the change for {name}",
  "portal.requests.approved": "Change approved for {name}.",
  "portal.requests.rejected": "Change rejected for {name}.",
  "portal.requests.rejectTitle": "Reject the change for {name}?",
  "portal.requests.rejectBody": "The member sees your note next to the request.",
  "portal.requests.note": "Note to the member (optional)",
  "portal.requests.empty": "empty",
  "portal.requests.cleared": "cleared",
  "portal.field.email": "Email",
  "portal.field.phone": "Phone",
  "portal.field.address": "Address",

  "portal.settings.title": "Member portal",
  "portal.settings.subtitle": "How members sign in, what they see, and how they pay. These apply to the member web portal and the apps.",
  "portal.settings.saved": "Portal settings saved.",
  "portal.settings.nothing": "Nothing changed.",
  "portal.settings.badAmount": "Enter an amount, such as 5.00.",
  "portal.settings.methods": "Ways to sign in",
  "portal.settings.methodsBody": "Turn on the ones you want. A member with two-step sign-in is asked for the code whichever way they sign in.",
  "portal.settings.password": "Email and password",
  "portal.settings.magic": "Email me a sign-in link",
  "portal.settings.pin": "Member number and PIN",
  "portal.settings.noEmail": "Outgoing email is not activated, so links cannot be sent. Set it up in Club settings, Communications.",
  "portal.settings.noSecrets": "This server has no secret key file yet, so PINs cannot be turned on.",
  "portal.settings.noMethod": "Turn on at least one way to sign in.",
  "portal.settings.magicTtl": "Sign-in link lasts (minutes)",
  "portal.settings.inviteTtl": "Invitation link lasts (hours)",
  "portal.settings.household": "Spouses and dependants",
  "portal.settings.householdBody": "Everyone on an account signs in as themselves. The account holder always sees the whole account.",
  "portal.settings.spouseSees": "A spouse sees",
  "portal.settings.dependentSees": "A dependant sees",
  "portal.settings.seesHousehold": "The whole account: balance, statements, all charges",
  "portal.settings.seesOwn": "Only the charges made to them",
  "portal.settings.spouseCanPay": "A spouse can pay the account online (when they see the whole account)",
  "portal.settings.dependentCanPay": "A dependant can pay the account online (when they see the whole account)",
  "portal.settings.profile": "Changes to contact details",
  "portal.settings.editMode": "When a member changes their email, phone or address",
  "portal.settings.editModeHint": "Either way the change is on record. During the parallel run with the old system, an edit lasts until the old system's record of that member changes.",
  "portal.settings.editRequest": "Staff approve it first",
  "portal.settings.editDirect": "It is saved at once",
  "portal.settings.directory": "Member directory",
  "portal.settings.directoryBody": "You decide what other members can see. Members can hide more, never less. Names are always shown for members who are listed; children are never listed.",
  "portal.settings.directoryOn": "Members can look each other up",
  "portal.settings.directoryFields": "Other members can see",
  "portal.directory.photo": "Photo",
  "portal.directory.member_number": "Member number",
  "portal.directory.email": "Email",
  "portal.directory.phone": "Phone",
  "portal.directory.address": "Home address",
  "portal.directory.member_since": "Year joined",
  "portal.directory.household": "Others on the account",
  "portal.settings.payments": "Online payment",
  "portal.settings.paymentsBody": "Members pay their bill by card. Card details go from the member's device to the payment service; this system never sees them. The payment service keys are kept in the server's configuration, not here. This server is in {mode} mode.",
  "portal.settings.processor": "Payment service",
  "portal.processor.none": "Not offered",
  "portal.processor.stripe": "Stripe",
  "portal.processor.creditline": "911 Software CreditLine (not available yet)",
  "portal.processor.fake": "Test service (development only)",
  "portal.settings.minimum": "Smallest payment",
  "portal.settings.minimumHint": "The last bit of a balance can always be paid.",
  "portal.settings.configured": "The payment service is set up on this server.",
  "portal.settings.notConfigured": "The payment service is not set up on this server yet, so members cannot pay online.",
  "portal.payments.title": "Payments needing attention",
  "portal.payments.subtitle": "Online payments the card already paid but the books keep refusing, and refunds and disputes the payment service reported. Nothing here changes the books by itself.",
  "portal.payments.none": "Nothing needs attention",
  "portal.payments.noneBody": "Every online payment has been posted, and none has been refunded or disputed.",
  "portal.payments.reason.postFailed": "The books refused it ({code})",
  "portal.payments.reason.refunded": "Refunded at the payment service",
  "portal.payments.reason.disputed": "Disputed by the cardholder ({status})",
  "portal.payments.refunded": "Refunded so far: {amount}",
  "portal.payments.capturedNote_one": "The card was charged. Tried {count} time to post it; automatic tries have stopped.",
  "portal.payments.capturedNote_other": "The card was charged. Tried {count} times to post it; automatic tries have stopped.",
  "portal.payments.retry": "Try posting again",
  "portal.payments.posted": "The payment was posted to the account.",
  "portal.payments.stillRefused": "The books still refuse it. Fix the cause and try again.",
  "portal.payments.resolve": "Mark as dealt with",
  "portal.payments.resolveTitle": "Payment for {name}",
  "portal.payments.resolveBody": "Record what was done, for example the refund entry made in the ledger. The payment leaves this list.",
  "portal.payments.note": "What was done",
  "portal.payments.resolved": "Marked as dealt with.",
  "portal.protected.label": "Kept from the Jonas import",
  "portal.protected.release": "Release {field}",
  "portal.protected.released": "Released. The next import will write Jonas's value.",
  "portal.settings.publicUrl": "Web address for links in emails",
  "portal.settings.publicUrlHint": "For example https://members.yourclub.example. Sign-in and reset links in emails start with this; it must be https and a host this server allows.",
  "portal.settings.noLinks": "Links cannot be made yet: set this address (or ask whoever runs the server to), otherwise no invitation or sign-in email is sent.",
  "portal.app.subtitle": "Publish galleries, pages, the staff directory, the menu, announcements and cottage rates. Members see a row only after it is published.",
  "portal.app.galleries": "Galleries",
  "portal.app.galleryName": "Gallery name",
  "portal.app.published": "published",
  "portal.app.hidden": "hidden",
  "portal.app.photoCount_one": "{count} photo",
  "portal.app.photoCount_other": "{count} photos",
  "portal.app.photoFor": "Photo for {name}",
  "portal.app.remove": "Remove",
  "portal.app.staffDirectory": "Staff directory",
  "portal.app.group": "Group",
  "portal.app.announcements": "Announcements",
  "portal.app.menu": "Menu",
  "portal.app.section": "Section",
  "portal.app.removeSection": "Remove section",
  "portal.app.publishedLabel": "Published",
  "portal.app.add": "Add",
  "portal.app.pages": "Pages",
  "portal.app.title": "Title",
  "portal.app.text": "Text",
  "portal.app.link": "Link",
  "portal.app.linkHint": "Optional. Use for a brochure. Must start with https://",
  "portal.app.addPage": "Add page",
  "portal.app.addAnnouncement": "Add announcement",
  "portal.app.name": "Name",
  "portal.app.jobTitle": "Title",
  "portal.app.phone": "Phone",
  "portal.app.addPerson": "Add person",
  "portal.app.removeGroup": "Remove group",
  "portal.app.item": "Item",
  "portal.app.opens": "Opens",
  "portal.app.choice": "Choice",
  "portal.app.choose": "Choose",
  "portal.app.addItem": "Add item",
  "portal.app.rateDefaultTitle": "Cottage rooms",
  "portal.app.rateDefaultHigh": "High season",
  "portal.app.rateDefaultLow": "Low season",
  "portal.app.rateCard": "Rate card",
  "portal.app.rateSheetError": "Each room line needs a name and two prices, separated by |.",
  "portal.app.firstSeason": "First season",
  "portal.app.firstSeasonMonths": "First season months",
  "portal.app.secondSeason": "Second season",
  "portal.app.secondSeasonMonths": "Second season months",
  "portal.app.rooms": "Rooms",
  "portal.app.roomsHint": "A line without | starts a group. A room line is: Single | 195 | 165",
  "portal.app.saveRateCard": "Save rate card",
} as const;

export type PortalMessages = typeof portalEn & typeof portalNavEn;
